import { useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useOnUpdate } from 'chaser-components';
import { useHistoryReducer } from './useGlobalState';

const Order = {
  NONE: 'NONE',
  ASC: 'ASC',
  DESC: 'DESC',
};

const TOGGLE_ORDERING = 'TOGGLE_ORDERING';
const SET_SORT = 'SET_SORT';
const SET_SEARCH = 'SET_SEARCH';
const SET_DEBOUNCED_SEARCH = 'SET_DEBOUNCED_SEARCH';
const SET_PAGE = 'SET_PAGE';
const SET_PAGE_SIZE = 'SET_PAGE_SIZE';
const ADD_FILTER = 'ADD_FILTER';
const REMOVE_FILTER = 'REMOVE_FILTER';
const CLEAR_FILTERS = 'CLEAR_FILTERS';
const SET_SELECTED = 'SET_SELECTED';

function useTableState({
  key = 'table',
  initialSortProp = 1,
  initialOrdering = Order.ASC,
  initialPageSize = 10,
  searchProp,
  debounceSearch = false,
} = {}) {
  function reducer(historyState, action) {
    const state = historyState[key];
    let newPartial;
    switch (action.type) {
      case TOGGLE_ORDERING:
        newPartial = {
          sortProp: action.payload,
          ordering:
            state.ordering !== Order.ASC || action.payload !== state.sortProp
              ? Order.ASC
              : Order.DESC,
          page: 1,
        };
        break;
      case SET_SORT:
        newPartial = {
          sortProp: action.payload.sortProp || state.sortProp,
          ordering: action.payload.ordering || state.ordering,
          page: 1,
        };
        break;
      case SET_SEARCH:
        if (debounceSearch) {
          newPartial = {
            search: action.payload,
          };
        } else {
          newPartial = {
            search: action.payload,
            ordering:
              state.sortProp === searchProp ? Order.NONE : state.ordering,
            page: 1,
          };
        }
        break;
      case SET_DEBOUNCED_SEARCH:
        newPartial = {
          debouncedSearch: action.payload,
          ordering: state.sortProp === searchProp ? Order.NONE : state.ordering,
          page: 1,
        };
        break;
      case SET_PAGE:
        newPartial = {
          page: action.payload,
        };
        break;
      case SET_PAGE_SIZE:
        newPartial = {
          pageSize: action.payload,
          page: 1,
        };
        break;
      case ADD_FILTER: {
        newPartial = {
          filters: [...state.filters, action.payload],
          page: 1,
          selected: {},
        };
        break;
      }
      case REMOVE_FILTER: {
        newPartial = {
          filters: state.filters.filter(f => f.id !== action.payload.id),
          page: 1,
          selected: {},
        };
        break;
      }
      case CLEAR_FILTERS: {
        newPartial = {
          filters: [],
          page: 1,
          selected: {},
        };
        break;
      }
      case SET_SELECTED: {
        newPartial = {
          selected: action.payload,
        };
        break;
      }

      default:
        throw new Error(`Unknown action "${action.type}"`);
    }

    return {
      ...historyState,
      [key]: {
        ...state,
        ...newPartial,
      },
    };
  }

  const [state, dispatch] = useHistoryReducer(reducer, {
    [key]: {
      debouncedSearch: '',
      search: '',
      sortProp: initialSortProp,
      ordering: initialOrdering,
      page: 1,
      pageSize: initialPageSize,
      filters: [],
      selected: {},
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedSearch = useCallback(
    debounce(
      value => dispatch({ type: 'SET_DEBOUNCED_SEARCH', payload: value }),
      500,
    ),
    [],
  );

  useOnUpdate(() => {
    if (debounceSearch && state[key].search !== state[key].debouncedSearch) {
      setDebouncedSearch(state[key].search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearch, setDebouncedSearch, state[key].search]);

  return [state[key], dispatch];
}

export default useTableState;
export {
  Order,
  SET_SORT,
  TOGGLE_ORDERING,
  SET_SEARCH,
  SET_PAGE,
  SET_PAGE_SIZE,
  ADD_FILTER,
  REMOVE_FILTER,
  CLEAR_FILTERS,
  SET_SELECTED,
};
